import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Anchor,
    Badge,
    Card,
    Container,
    Grid,
    Group,
    List,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
    useMantineTheme
} from '@mantine/core';
import {IconExternalLink} from '@tabler/icons-react';
import {useConfig, NewsItem} from '../ConfigContext';
import ArticleImage from '../components/ArticleImage';
import classes from './News.module.css';

function News() {
    const theme = useMantineTheme();
    const {newsCategories, news} = useConfig();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [filteredNews, setFilteredNews] = useState(news);
    const [selectedNewsItem, setSelectedNewsItem] = useState<NewsItem | null>(null);
    const {assetId} = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (assetId) {
            const newsItem = news.find(item => item.assetId === assetId);
            if (newsItem) {
                setSelectedNewsItem(newsItem);
            } else {
                navigate('/news');
            }
        } else {
            setSelectedNewsItem(null);
        }
    }, [assetId, news, navigate]);

    useEffect(() => {
        const lowercaseFilter = searchQuery.toLowerCase();
        const filteredBySearch = news.filter(item =>
            item.title.toLowerCase().includes(lowercaseFilter) ||
            item.abstract?.toLowerCase().includes(lowercaseFilter) ||
            item.categoryId.toLowerCase().includes(lowercaseFilter)
        );

        const filteredByCategory = selectedCategory
            ? filteredBySearch.filter(item => item.categoryId === selectedCategory)
            : filteredBySearch;

        setFilteredNews(filteredByCategory);
    }, [searchQuery, selectedCategory, news]);

    const handleSelectNewsItem = (item: NewsItem) => {
        navigate(`/news/${item.assetId}`);
    };

    const renderNewsItem = (item: NewsItem, index: number) => (
        <Card
            withBorder
            radius="md"
            className={classes.card}
            key={item.assetId}
            onClick={() => handleSelectNewsItem(item)}
            style={{cursor: 'pointer'}}
        >
            <Card.Section p="xs">
                <ArticleImage
                    src={item.image}
                    alt={item.title}
                    scene={item.scene}
                    tone={item.tone}
                    seed={index}
                />
            </Card.Section>
            <Group gap="xs" mt="xs">
                <Badge variant="light" color="gray">{newsCategories[item.categoryId]?.title}</Badge>
                {item.date && <Badge variant="light" color="gray">{item.date}</Badge>}
            </Group>
            <Text className={classes.title} fw={600} style={{marginTop: 10}}>
                {item.title}
            </Text>
            <Text fz="sm" c="dimmed" lineClamp={4}>
                {item.abstract}
            </Text>
        </Card>
    );

    const renderNewsItemKeyPoints = (item: NewsItem) => (
        <List spacing="md" size="sm" style={{padding: 25}}>
            {item.keyPoints?.map((point, idx) =>
                <List.Item key={idx}>
                    <Text
                        fw={600}
                        className={classes.keyPoints}
                        style={{display: 'inline', marginRight: '3px'}}
                    >
                        {point.split('|')[0]}
                    </Text>
                    <Text
                        fw={300}
                        className={classes.keyPoints}
                        style={{display: 'inline'}}
                    >
                        {point.split('|')[1]}
                    </Text>
                </List.Item>)}
        </List>
    );

    const renderSources = (item: NewsItem) => {
        if (!item.sources?.length) return null;
        return (
            <Stack gap="xs" mt="xl">
                <Text fw={700} size="sm">Sources</Text>
                {item.sources.map((source) => (
                    <Anchor
                        key={source.href}
                        href={source.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                    >
                        <Group gap={6} wrap="nowrap" align="center">
                            <IconExternalLink size={14} stroke={1.5}/>
                            {source.label}
                        </Group>
                    </Anchor>
                ))}
            </Stack>
        );
    };

    return (
        <Container py="xl" size="xl">
            <Stack gap="sm" mb="xl">
                <Title order={1} c={theme.colors.brand[6]}>Thinking</Title>
                <Text size="lg" maw={760}>
                    What we are reading, arguing about, and building against — governance,
                    voice, grounding, and the business of running a studio.
                </Text>
            </Stack>

            <TextInput
                radius="xl"
                placeholder="Search by title, topic, or category"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.currentTarget.value)}
                style={{marginBottom: '20px'}}
            />
            <Group style={{justifyContent: 'center', margin: '10px 0'}}>
                {Object.keys(newsCategories).map((category) => (
                    <Badge
                        key={category}
                        color={selectedCategory === category ? 'blue' : 'gray'}
                        onClick={() => setSelectedCategory(category)}
                        style={{cursor: 'pointer'}}
                    >
                        {newsCategories[category].title}
                    </Badge>
                ))}
                <Badge color="red" onClick={() => setSelectedCategory('')} style={{cursor: 'pointer'}}>
                    Clear Filter
                </Badge>
            </Group>
            <Grid>
                {selectedNewsItem ? (
                    <>
                        <Grid.Col span={{base: 12, md: 9}}>
                            <Card withBorder radius="md" style={{padding: '20px'}}>
                                <ArticleImage
                                    src={selectedNewsItem.image}
                                    alt={selectedNewsItem.title}
                                    scene={selectedNewsItem.scene}
                                    tone={selectedNewsItem.tone}
                                />
                                <Group gap="xs" mt="md">
                                    <Badge variant="light" color="gray">
                                        {newsCategories[selectedNewsItem.categoryId]?.title}
                                    </Badge>
                                    {selectedNewsItem.date && (
                                        <Badge variant="light" color="gray">{selectedNewsItem.date}</Badge>
                                    )}
                                </Group>
                                <Title order={2} className={classes.title} mt="sm">
                                    {selectedNewsItem.title}
                                </Title>
                                <Text size="md" className={classes.intro} mt="md">
                                    {selectedNewsItem.intro}
                                </Text>
                                {renderNewsItemKeyPoints(selectedNewsItem)}
                                <Text size="md" className={classes.cta}>{selectedNewsItem.cta}</Text>
                                <Text size="md" className={classes.conclusion}>{selectedNewsItem.conclusion}</Text>
                                {renderSources(selectedNewsItem)}
                            </Card>
                        </Grid.Col>
                        <Grid.Col span={{base: 12, md: 3}}>
                            <SimpleGrid cols={1}>
                                {filteredNews.map(renderNewsItem)}
                            </SimpleGrid>
                        </Grid.Col>
                    </>
                ) : (
                    <Grid.Col span={12}>
                        <SimpleGrid cols={{base: 1, sm: 2, lg: 3}} spacing="lg">
                            {filteredNews.map(renderNewsItem)}
                        </SimpleGrid>
                    </Grid.Col>
                )}
            </Grid>
        </Container>
    );
}

export default News;
